import { Box, Tooltip, Typography } from '@mui/material';
import { Check } from 'lucide-react';
import { useIntl } from 'react-intl';
import type { ReplanningStageId } from '~/state/atoms/adaptiveReplanningAtoms';
import { REPLANNING_STAGES } from './replanningStages';
import type { StageStatus } from './useReplanningStageStatus';

interface ReplanningStageNavigatorProps {
    currentStageId: ReplanningStageId;
    stageStates: readonly StageStatus[];
}

const STEP_SIZE = 26;

export default function ReplanningStageNavigator({
    currentStageId,
    stageStates,
}: ReplanningStageNavigatorProps) {
    const intl = useIntl();
    const index = REPLANNING_STAGES.findIndex(
        (stage) => stage.id === currentStageId
    );
    const current = REPLANNING_STAGES[index] ?? REPLANNING_STAGES[0];

    return (
        <Box px={1.5} pt={1} data-testid="replanning-stage-navigator">
            <Box display="flex" alignItems="center" gap={0.5} role="list">
                {REPLANNING_STAGES.map((stage, position) => {
                    const label = intl.formatMessage({ id: stage.labelId });
                    const isCurrent = stage.id === currentStageId;
                    const state = stageStates.find(
                        (entry) => entry.stageId === stage.id
                    )?.state;
                    const blocked = state === 'blocked';
                    const isComplete = state === 'complete';

                    return (
                        <Box
                            key={stage.id}
                            display="flex"
                            alignItems="center"
                            flex={position === 0 ? '0 0 auto' : 1}
                            minWidth={0}
                            gap={0.5}
                        >
                            {position > 0 && (
                                <Box
                                    aria-hidden
                                    flex={1}
                                    minWidth={8}
                                    height="2px"
                                    bgcolor="divider"
                                />
                            )}
                            <Tooltip title={label} placement="bottom">
                                <Box
                                    role="listitem"
                                    aria-label={label}
                                    aria-current={
                                        isCurrent ? 'step' : undefined
                                    }
                                    data-testid={`replanning-stage-${stage.id}`}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        width: STEP_SIZE,
                                        height: STEP_SIZE,
                                        flexShrink: 0,
                                        borderRadius: '50%',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        border: '1px solid',
                                        borderColor: isCurrent
                                            ? 'primary.main'
                                            : isComplete
                                              ? 'success.main'
                                              : 'divider',
                                        bgcolor: isCurrent
                                            ? 'primary.main'
                                            : 'transparent',
                                        color: isCurrent
                                            ? 'primary.contrastText'
                                            : blocked
                                              ? 'text.disabled'
                                              : isComplete
                                                ? 'success.main'
                                                : 'text.secondary',
                                        opacity: blocked ? 0.5 : 1,
                                    }}
                                >
                                    {isComplete ? (
                                        <Check size={14} aria-hidden />
                                    ) : (
                                        position + 1
                                    )}
                                </Box>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>

            <Typography fontSize={12.5} fontWeight={600} pt={0.75}>
                {intl.formatMessage({ id: current.labelId })}
            </Typography>
        </Box>
    );
}
